import React from 'react';

/**
 * Shimmer skeleton primitives used across the AdminConsole tabs.
 *
 * Replaces the `<Loader2 spin>` data-loading placeholders with content-shaped
 * pulses so the page doesn't reflow when data lands. Cards and rows match the
 * eventual element dimensions (height, padding, rounded corners).
 *
 * Pattern ported from Nick Velten's vanilla admin reference (basgosewisch/stagery-ai @ 7e6e63b)
 * but expressed via Tailwind `animate-pulse` instead of custom CSS keyframes.
 */

export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-ink/[0.06] dark:bg-white/10 rounded ${className}`} />
);

/** 4-up stat-card grid skeleton matching StatsSection layout. */
export const StatCardsSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="bg-white dark:bg-ink/40 rounded-xl border border-black/[0.06] dark:border-white/10 shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="w-4 h-4 rounded-md" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-7 w-16" />
      </div>
    ))}
  </div>
);

/** Table-row skeleton with configurable column count. Matches existing 9-tab table density. */
export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({ rows = 8, cols = 6 }) => (
  <div className="px-4 py-3">
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, ri) => (
        <div key={ri} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }).map((__, ci) => (
            <Skeleton key={ci} className="h-4" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/** Single feedback-card skeleton stack. */
export const FeedbackSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div className="divide-y divide-black/[0.05] dark:divide-white/10">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    ))}
  </div>
);
